import { useElementBounding, useWindowScroll } from "@vueuse/core";
import { defineStore } from "pinia";
import { type Ref, ref, shallowReactive, toRefs, watch } from "vue";

interface FirstArticleBounding {
  top: Ref<number>;
  left: Ref<number>;

  width: Ref<number>;
  height: Ref<number>;
}

interface ArticlesBounding {
  bottom: Ref<number>;
  left: Ref<number>;
}

// All values that are affected of vertical (y) window scroll
const firstArticleTop = ref<number>(0);
const articlesBottom = ref<number>(0);

interface State {
  firstArticle: FirstArticleBounding | null;
  articles: ArticlesBounding | null;
}

const windowScroll = useWindowScroll();

export const useElementRegistryStore = defineStore("element-registry", () => {
  const state = shallowReactive<State>({
    firstArticle: null,
    articles: null
  });

  const registerArticles = (articles: HTMLElement) => {
    reigsterFirstArticle(articles);

    const articlesElementBounding = useElementBounding(articles);

    watch(
      articlesElementBounding.bottom,
      (bottomValue) => {
        if (bottomValue) {
          articlesBottom.value = bottomValue + windowScroll.y.value;
        }
      },
      { immediate: true }
    );

    watch(
      windowScroll.y,
      (yValue) => {
        if (yValue) {
          articlesBottom.value = articlesElementBounding.bottom.value + yValue;
        }
      },
      { immediate: true }
    );

    state.articles = {
      bottom: articlesBottom,
      left: articlesElementBounding.left
    };
  };

  const reigsterFirstArticle = (articles: HTMLElement) => {
    const firstArticle: HTMLElement | null = articles.querySelector("article.article-content");

    if (!firstArticle) {
      return;
    }

    const firstArticleElementBounding = useElementBounding(firstArticle);

    watch(
      firstArticleElementBounding.top,
      (topValue) => {
        if (topValue) {
          firstArticleTop.value = topValue + windowScroll.y.value;
        }
      },
      { immediate: true }
    );

    watch(
      windowScroll.y,
      (yValue) => {
        if (yValue) {
          firstArticleTop.value = firstArticleElementBounding.top.value + yValue;
        }
      },
      { immediate: true }
    );

    state.firstArticle = {
      top: firstArticleTop,
      left: firstArticleElementBounding.left,

      width: firstArticleElementBounding.width,
      height: firstArticleElementBounding.height
    };
  };

  return {
    ...toRefs(state),
    registerArticles
  };
});
